import { useQuery } from "@apollo/client";
import { Box, Spinner, Text } from "grommet";
import { useEffect } from "react";
import FlipMove from "react-flip-move";
import { GET_TIP_QUEUE } from "../../queries/tipQueue";
import { TipQueue, TipQueueEntry } from "../../types/TipQueue";
import RecentTipItem from "../RecentTipItem";

type Props = {
  isTipNotesOnly: boolean;
};

// The OBS browser-source widget: a read-only mirror of the tip queue, which
// mods work from the Tip Queue page. Waiting tips first, then banked ones.
// The notes-only route is the older "read the notes" feed: every open tip
// with a note, whether or not it buys a menu item.
const RecentTips = (props: Props) => {
  const { isTipNotesOnly } = props;
  const { loading, error, data, startPolling } = useQuery(GET_TIP_QUEUE, {
    variables: { broadcaster: "crimsonkitten" },
  });

  useEffect(() => {
    startPolling(2000);
  }, [startPolling]);

  if (error) {
    return (
      <Box round align="center" flex="shrink">
        <Box
          background={"status-critical"}
          round
          pad="small"
          animation={"pulse"}
        >
          <Text>Error: {error.message}</Text>
        </Box>
      </Box>
    );
  }

  const queue: TipQueue | undefined = data?.getTipQueue;
  if (loading && !queue) {
    return (
      <Box align="center" margin={"large"}>
        <Spinner size="large" />
      </Box>
    );
  }

  const waiting: TipQueueEntry[] = isTipNotesOnly
    ? (queue?.notes ?? [])
    : (queue?.waiting ?? []);
  const banked: TipQueueEntry[] = isTipNotesOnly ? [] : (queue?.banked ?? []);

  return (
    <div className="bg-base-200 h-full flex flex-col gap-2">
      <FlipMove
        className="flex flex-row flex-wrap gap-2 justify-center"
        duration={100}
        maintainContainerHeight={true}
      >
        {waiting.map((entry) => (
          <RecentTipItem
            key={entry.id}
            entry={entry}
            isTipNotesOnly={isTipNotesOnly}
          />
        ))}
      </FlipMove>
      {banked.length > 0 && (
        <>
          <div className="text-center text-xs uppercase tracking-wide opacity-70">
            Banked
          </div>
          <FlipMove
            className="flex flex-row flex-wrap gap-2 justify-center"
            duration={100}
            maintainContainerHeight={true}
          >
            {banked.map((entry) => (
              <RecentTipItem
                key={entry.id}
                entry={entry}
                isTipNotesOnly={isTipNotesOnly}
              />
            ))}
          </FlipMove>
        </>
      )}
    </div>
  );
};

export default RecentTips;
